import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { IconRiders } from '../components/icons';
import { Avatar, Chips, DataTable, Empty, ErrorNote, Loading, PageHead, Pager, Pill, RowCard, SearchField } from '../components/ui';
import { api } from '../lib/api';
import { day, phone } from '../lib/format';
import { useDashboard } from '../lib/queries';
import { qk } from '../lib/realtime-cache';
import type { Page, RiderListItem } from '../lib/types';
import { ridersRoute } from '../router';

export function RidersPage() {
  const search = ridersRoute.useSearch();
  const nav = ridersRoute.useNavigate();
  const navigate = useNavigate();
  const dash = useDashboard();
  const page = search.page ?? 1;
  const [text, setText] = useState(search.q ?? '');
  useEffect(() => {
    const t = setTimeout(() => {
      if ((search.q ?? '') !== text.trim()) void nav({ search: { ...search, q: text.trim() || undefined, page: undefined } });
    }, 300);
    return () => clearTimeout(t);
  }, [text, search, nav]);

  const q = useQuery({
    queryKey: [...qk.riders, search],
    queryFn: () =>
      api.get<Page<RiderListItem>>('/admin/riders', { search: search.q, blocked: search.blocked ? 'true' : undefined, page, pageSize: 25 }),
    placeholderData: keepPreviousData,
  });
  const open = (r: RiderListItem) => navigate({ to: '/riders/$riderId', params: { riderId: r.id } });
  const status = (r: RiderListItem) => (r.isBlocked ? <Pill tone="bad">Blocked</Pill> : <Pill tone="good">Active</Pill>);

  return (
    <>
      <PageHead title="Riders" sub={dash.data ? `${dash.data.riders.toLocaleString('en-IN')} people who book rides` : 'People who book rides.'} />
      <div className="toolbar">
        <Chips
          label="Show"
          value={search.blocked ? 'blocked' : 'all'}
          options={[
            { value: 'all', label: 'Everyone' },
            { value: 'blocked', label: 'Blocked' },
          ]}
          onChange={(v) => nav({ search: { ...search, blocked: v === 'blocked' ? true : undefined, page: undefined } })}
        />
        <SearchField value={text} onChange={setText} placeholder="Search name, phone or email" label="Search riders" />
      </div>
      <section className="card">
        {q.isLoading ? (
          <Loading rows={6} />
        ) : q.error ? (
          <div className="card-body">
            <ErrorNote error={q.error} />
          </div>
        ) : !q.data?.items.length ? (
          <Empty title="No riders found" icon={IconRiders}>
            Try another search.
          </Empty>
        ) : (
          <>
            <DataTable
              label="Riders"
              rows={q.data.items}
              rowKey={(r) => r.id}
              onRowClick={open}
              columns={[
                {
                  key: 'rider',
                  header: 'Rider',
                  cell: (r) => (
                    <div className="person">
                      <Avatar name={r.name} />
                      <div style={{ minWidth: 0 }}>
                        <div className="primary truncate">{r.name ?? 'No name yet'}</div>
                        {r.email && <div className="secondary truncate">{r.email}</div>}
                      </div>
                    </div>
                  ),
                },
                { key: 'phone', header: 'Phone', className: 'nowrap', cell: (r) => (r.phone ? phone(r.phone) : <span className="faint">Email only</span>) },
                { key: 'rating', header: 'Rating', cell: (r) => (r.rating != null ? `★ ${r.rating.toFixed(1)}` : <span className="faint">—</span>) },
                { key: 'joined', header: 'Joined', className: 'nowrap wide-only', cell: (r) => day(r.createdAt) },
                { key: 'status', header: 'Status', cell: status },
              ]}
              card={(r) => (
                <RowCard
                  lead={<Avatar name={r.name} />}
                  title={r.name ?? 'No name yet'}
                  sub={`${r.phone ? phone(r.phone) : (r.email ?? '—')} · joined ${day(r.createdAt)}`}
                  side={status(r)}
                />
              )}
            />
            <Pager page={page} pageSize={q.data.pageSize} total={q.data.total} onPage={(p) => nav({ search: { ...search, page: p } })} />
          </>
        )}
      </section>
    </>
  );
}
